/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import type { PoiContentSlide } from '@/types/shop';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { AppUtils } from '@/utils/app';
import { useEffect, useState } from 'react';

interface FacilityCarouselItemProps {
    content: PoiContentSlide;
    tabIndex: number;
    onClick?: (data: PoiContentSlide) => void;
}

const FacilityCarouselItem = ({ content, tabIndex, onClick }: FacilityCarouselItemProps) => {
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();
    const [hasImageError, setHasImageError] = useState(false);

    useEffect(() => {
        setHasImageError(false);
    }, [content.url]);

    const handleClick = (e: any) => {
        const parent = e.currentTarget as HTMLElement;
        parent.classList.add('poi-active');
        handleInteraction(e, () => onClick?.(content));
    };

    return (
        <div
            key={content.id}
            tabIndex={tabIndex}
            data-interactive
            className={clsx(styles.wrapper, {
                [styles['wrapper__vertical']]: !isHorizontal,
                [styles['wrapper__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
            })}
            data-role={t('common.button.tts.popup_button')}
            data-playsound="ting"
            aria-label={`${content.name}, ${buildings?.length && buildings.length > 1 ? content.buildingName + ', ' : ''} ${t('shop.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(content.floor) })}`}
            onClick={handleClick}
            onKeyDown={(e) => handleKeyboardInteraction(e, () => onClick?.(content))}
        >
            {/* Poi building and floor */}
            <div className={clsx(styles['wrapper__poi--info'], { [styles['wrapper__poi--info__contrast']]: contrast })}>
                {buildings?.length && buildings.length > 1 && (
                    <>
                        <span className={styles['wrapper__poi--info--building-name']}>{content.buildingName}</span>
                        <div className={styles['wrapper__poi--info--divider']}></div>
                    </>
                )}
                <span className={clsx('number', styles['wrapper__poi--info--floor'])}>{content.floor}</span>
            </div>
            {/* Poi Image */}
            {(screenMode === 'fullscreen' || (screenMode === 'split' && isHorizontal)) && (
                <div className={styles['wrapper__poi--image']}>
                    {content.url && !hasImageError ? (
                        <img
                            src={content.url}
                            alt={`${content.name} logo`}
                            loading="lazy"
                            data-role={t('common.data_role.image')}
                            onError={() => setHasImageError(true)}
                        />
                    ) : (
                        <PlaceHolder />
                    )}
                </div>
            )}

            {/* Poi Info */}
            <div className={clsx(styles['wrapper__poi--name'], { [styles['wrapper__poi--name__contrast']]: contrast })}>
                <h3>{content.name}</h3>
            </div>
        </div>
    );
};

export default FacilityCarouselItem;
